import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLock } from '../context/LockContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import RestaurantSelector from './RestaurantSelector';
import UserMenu from './UserMenu';
import LanguageSelector from './LanguageSelector';
import ViewToggle from './ViewToggle';
import './Header.css';

function Header({ viewMode, canToggle, onSetViewMode, showAllAreas, onToggleAllAreas, canShowAllAreas, userRole }) {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasPin, lock } = useLock();
  const { preference, cyclePreference } = useTheme();
  const { showToast } = useToast();

  const handleToggleTheme = useCallback(() => {
    const order = ['light', 'dark', 'auto'];
    const next = order[(order.indexOf(preference) + 1) % order.length];
    cyclePreference();
    showToast(t('toast.themeChanged', { mode: t(`theme.${next}`) }), 'info');
  }, [cyclePreference, preference, t, showToast]);

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };

  const handleLock = () => {
    lock();
  };

  return (
    <header className="header">
      <div className="header-left">
        <RestaurantSelector />
      </div>

      <nav className="header-center" aria-label={t('header.viewNavigation')}>
        {userRole && !['OWNER', 'ADMIN'].includes(userRole) && (
          <span className="header-role-badge">{t(`header.role.${userRole.toLowerCase()}`, userRole)}</span>
        )}
        <ViewToggle viewMode={viewMode} canToggle={canToggle} setViewMode={onSetViewMode} />
        {canShowAllAreas && (
          <button
            className={`header-show-all-btn ${showAllAreas ? 'active' : ''}`}
            onClick={onToggleAllAreas}
            title={t('header.showAllAreas')}
            aria-pressed={showAllAreas}
          >
            <span className="material-icons" aria-hidden="true">
              {showAllAreas ? 'visibility' : 'visibility_off'}
            </span>
            <span>{t('header.showAllAreas')}</span>
          </button>
        )}
      </nav>

      <div className="header-right">
        <LanguageSelector compact />
        <button
          className="header-icon-btn"
          onClick={handleToggleTheme}
          aria-label={t(`theme.${preference}`)}
          title={t(`theme.${preference}`)}
        >
          {preference === 'light' && <Sun size={18} aria-hidden="true" />}
          {preference === 'dark' && <Moon size={18} aria-hidden="true" />}
          {preference === 'auto' && <Monitor size={18} aria-hidden="true" />}
        </button>
        {hasPin && (
          <button
            className="header-icon-btn"
            onClick={handleLock}
            aria-label={t('header.lockScreen')}
            title={t('header.lockScreen')}
          >
            🔒
          </button>
        )}
        <div className="user-menu-container">
          <button
            className="user-menu-btn"
            onClick={toggleUserMenu}
            aria-label={t('header.userMenu')}
          >
            <div className="header-user-avatar">
              {user?.attributes?.email?.charAt(0).toUpperCase() ||
               user?.username?.charAt(0).toUpperCase() ||
               'U'}
            </div>
            <span className="user-name">
              {user?.attributes?.name || user?.attributes?.email?.split('@')[0] || user?.username || ''}
            </span>
            <span className={`dropdown-arrow ${isUserMenuOpen ? 'open' : ''}`}>▼</span>
          </button>

          {isUserMenuOpen && (
            <UserMenu onClose={closeUserMenu} />
          )}
        </div>
      </div>

      {isUserMenuOpen && (
        <div className="menu-overlay" onClick={closeUserMenu}></div>
      )}
    </header>
  );
}

export default Header;
